/**
 * Shared model-source helpers for the AI block config panels.
 *
 * Fixed providers expose a hardcoded catalog (`availableAIProviders[p].models`).
 * Self-hosted providers (LM Studio, flagged `dynamic_models`) instead carry a
 * single selected model + its probed capabilities as a snapshot on the project
 * provider config. These helpers abstract that difference so the pickers don't
 * branch on provider type. Requires `availableAIProviders` (state) and the
 * `getProjectAIProviders` getter on the host component.
 */
export default {
  methods: {
    isDynamicProvider(provider) {
      return this.availableAIProviders?.[provider]?.dynamic_models === true;
    },
    // All model objects for a provider, fixed catalog or dynamic snapshot.
    modelsForProvider(provider) {
      const avail = this.availableAIProviders?.[provider];
      if (!avail) return [];
      if (avail.dynamic_models) {
        const cfg = (this.getProjectAIProviders && this.getProjectAIProviders()[provider]) || null;
        const meta = cfg?.config?.model_meta;
        if (!meta) return [];
        return [{ ...meta, model: meta.id, cost: 'Self-hosted' }];
      }
      return Object.values(avail.models || {});
    },
    providerHasModelWithFeatures(provider, requiredFeatures) {
      return this.modelsForProvider(provider).some((m) => (requiredFeatures || []).every((f) => (m.features || []).includes(f)));
    },
    // Runtime-bound config snapshot for the selected model (published with the project).
    buildDynamicAwareModelConfig(provider, modelId) {
      const avail = this.availableAIProviders?.[provider];
      const m = this.modelsForProvider(provider).find((x) => x.model === modelId);
      if (!m) return null;
      return {
        ...JSON.parse(JSON.stringify(m)),
        provider,
        max_tokens_param: avail?.max_tokens_param || m.max_tokens_param || 'max_tokens',
      };
    },
  },
};
